import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, ErrorHandler } from '@angular/core';
import { NavController, Platform } from '@ionic/angular';
import { environment } from '../../environments/environment';
import { ToastService } from './toast.service';
import { AppUrls } from '../portal/routing.state';
import { BaseResponse } from '../shared';
import { tap } from 'rxjs';
import { Capacitor } from '@capacitor/core';

interface LogErrorResponse extends BaseResponse {
	show_error: string;
}

@Injectable({
	providedIn: 'root',
})
export class FmErrorHandlerService extends ErrorHandler implements ErrorHandler {
	constructor(
		private navController: NavController,
		private toastService: ToastService,
		private platform: Platform,
		private http: HttpClient
	) {
		super();
	}

	private isInAppMode() {
		let isApp = false;
		const isCapacitor = Capacitor.isNativePlatform();
		if (isCapacitor && (this.platform.is('ios') || this.platform.is('android'))) {
			isApp = true;
		}
		return isApp;
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	override async handleError(error: any) {
		if (this.isErrorHandled(error)) {
			console.log('[ERROR handled!] ', error);
			return;
		}
		const errorMessage = error.message ?? error;
		console.log('[ERROR!] ', errorMessage);

		// const messagePrefix = 'Something went wrong!';
		// const message = this.parseError(error);
		// const isApp = this.isInAppMode();
		// this.toastService.showToast(isApp, {
		// 	message: message ? `${message}` : messagePrefix,
		// 	position: 'top',
		// 	buttons: [{ side: 'end', text: 'Ok' }],
		// 	duration: 5000, // autoClose in 5 seconds
		// });

		this.recordError(error);

		// TODO: log in a log service
		// Make sure to rethrow the error so Angular can pick it up
		// throw error;
		super.handleError(error);
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	handleHttpInterceptorError(error: any) {
		console.log('[HTTP ERROR!] ', error);

		this.handleForbidden(error);

		this.handleNoHttpResponse(error);

		this.handleApiStatus(error);

		// Do not trigger UI error from interceptor
		// If we have an http call where we want to catch the error inside the service and handle it there
		// We dont want in this case that the global interceptor to show an error
		// eg: service.getId().pipe(catchError() => customLogic)
		// Unhandled errors will enter in the custom error handler and show the errors
		// We are gonna still keep the interceptor for a while for custom generic errors on API calls

		this.handleError(error);
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private isErrorHandled(error: any) {
		if (!error) {
			// do not log empty error
			return true;
		}
		return (
			error?.status === 0 ||
			error?.status === 200 ||
			error?.status === 403 ||
			error?.currentTarget?.status === 0 ||
			error?.currentTarget?.status === 200 ||
			error?.currentTarget?.status === 403
		);
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private async handleNoHttpResponse(error: any) {
		if (error?.status === 0 || error?.currentTarget?.status === 0) {
			const isApp = this.isInAppMode();
			this.toastService.showToast(isApp, {
				message: 'Could not connect to the server! Please check internet connection and reload the application!',
				position: 'top',
				buttons: [{ side: 'end', text: 'Ok' }],
				duration: 5000, // autoClose in 5 seconds
			});
			this.navController.navigateRoot(AppUrls.NO_INTERNET);
		}
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private async handleForbidden(error: any) {
		if (error?.status === 403 || error?.currentTarget?.status === 403) {
			const isApp = this.isInAppMode();
			this.toastService.showToast(isApp, {
				message: 'Forbidden access! Try to relogin or contact support!',
				position: 'top',
				buttons: [{ side: 'end', text: 'Ok' }],
				duration: 5000, // autoClose in 5 seconds
			});
			this.navController.navigateRoot(AppUrls.INSIDE_HOME);
		}
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private async handleApiStatus(error: any) {
		if (error?.status === 200 || error?.currentTarget?.status === 200) {
			const isApp = this.isInAppMode();
			this.toastService.showToast(isApp, {
				message: error.message || error,
				position: 'top',
				buttons: [{ side: 'end', text: 'Ok' }],
				duration: 5000, // autoClose in 5 seconds
			});
		}
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private parseError(error: any) {
		if (typeof error === 'string') {
			return error;
		}
		if (error?.error instanceof ErrorEvent) {
			return error.error.message;
		}
		if (error instanceof HttpErrorResponse) {
			return this.getHttpMessage(error);
		}
		if (typeof error === 'object') {
			return this.parseObjectMessage(error);
		}
		return error?.message;
	}

	private getHttpMessage(error: HttpErrorResponse) {
		if (typeof error.error?.message === 'string') {
			return error.error.message;
		}
		if (error.error.message?.isJoi) {
			return error.error.message.details[0]?.message;
		}
		if (error.error.message?.details[0]?.message) {
			return error.error.message.details[0]?.message;
		}
		return error.statusText;
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private parseObjectMessage(error: any) {
		if (typeof error?.message === 'string') {
			return error.message;
		}
		if (error.message?.isJoi) {
			return error.message.details[0]?.message;
		}
		return error;
	}

	private async recordError(error: any) {
		try {
			const pageUrl = window.location.href;
			const serialized = this.serializeError(error);
			const jsonError = JSON.stringify(serialized);

			console.log('Error occured, logging to sever');

			const data = new FormData();
			data.append('error_json', jsonError);
			data.append('page_url', pageUrl);
			this.http
				.post<LogErrorResponse>(`${environment.apiUrl}/system/app-error`, data)
				.pipe(
					tap((response) => {
						if (!serialized.dontShow) {
							this.toastService.showToast(this.isInAppMode(), {
								message: response?.show_error && response.message ? response.message : serialized.message,
								position: 'top',
								buttons: [{ side: 'end', text: 'Ok' }],
								duration: 5000, // autoClose in 5 seconds
							});
						}
					})
				)
				.subscribe();
		} catch (err) {
			console.log('failed to record error');
			console.error(err);
		}
	}

	private serializeError(error: any) {
		if (error?.rejection) {
			error = error.rejection; // Extract actual error from promise rejection
		}

		if (typeof error !== 'object' || error === null) {
			return { message: String(error) };
		}

		const { message, name, stack } = error;

		const serializedError: any = {
			message: message || 'Unknown error',
			name: name || 'Error',
			stack: stack || 'No stack trace available',
		};

		// If error has additional properties, include them
		for (const key in error) {
			if (!serializedError[key] && Object.prototype.hasOwnProperty.call(error, key)) {
				serializedError[key] = error[key];
			}
		}

		return serializedError;
	}
}
